// src/pages/NomConceptosPage.jsx
import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerConceptos, obtenerConcepto, crearConcepto, actualizarConcepto, eliminarConcepto,
} from "../services/nominaService";

const EMPTY = {
  Clave: "", Nombre: "", TipoConcepto: "P", ClaveSat: "",
  GravaImss: false, GravaIsr: false, Activo: true,
};

const TIPO_LABEL = { P: "Percepción", D: "Deducción", O: "Otro" };

const TIPO_BADGE = {
  P: { bg: "#dcfce7", color: "#16a34a" },
  D: { bg: "#fee2e2", color: "#dc2626" },
  O: { bg: "#f1f5f9", color: "#475569" },
};

function Check({ ok }) {
  return ok
    ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span>
    : <span style={{ color: "#cbd5e1" }}>—</span>;
}

export default function NomConceptosPage() {
  const [rows, setRows] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    obtenerConceptos(filtroTipo || null)
      .then((data) => { if (!cancelled) setRows(data ?? []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filtroTipo, reloadKey]);

  const filtered = rows.filter((r) =>
    !search || (r.clave ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (r.nombre ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => { setEditId(null); setForm(EMPTY); setShowModal(true); };

  const openEdit = async (id) => {
    try {
      const c = await obtenerConcepto(id);
      setForm({ Clave: c.clave ?? "", Nombre: c.nombre ?? "", TipoConcepto: c.tipoConcepto ?? "P", ClaveSat: c.claveSat ?? "", GravaImss: c.gravaImss ?? false, GravaIsr: c.gravaIsr ?? false, Activo: c.activo ?? true });
      setEditId(id);
      setShowModal(true);
    } catch {
      toastError("Error al cargar concepto");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este concepto?")) return;
    try {
      await eliminarConcepto(id);
      toastSuccess("Concepto eliminado");
      reload();
    } catch {
      toastError("No se pudo eliminar");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await actualizarConcepto(editId, form);
        toastSuccess("Concepto actualizado");
      } else {
        await crearConcepto(form);
        toastSuccess("Concepto creado");
      }
      setShowModal(false);
      reload();
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "clave", title: "Clave", style: { width: 90, fontWeight: 600 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "tipoConcepto", title: "Tipo",
      render: (r) => {
        const s = TIPO_BADGE[r.tipoConcepto] ?? TIPO_BADGE.O;
        return (
          <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>
            {TIPO_LABEL[r.tipoConcepto] ?? r.tipoConcepto}
          </span>
        );
      },
      style: { width: 110 },
    },
    { key: "claveSat", title: "Clave SAT", style: { width: 100 } },
    { key: "gravaImss", title: "IMSS", render: (r) => <Check ok={r.gravaImss} />, style: { width: 60, textAlign: "center" } },
    { key: "gravaIsr", title: "ISR", render: (r) => <Check ok={r.gravaIsr} />, style: { width: 60, textAlign: "center" } },
    {
      key: "activo", title: "Activo",
      render: (r) => <Check ok={r.activo} />,
      style: { width: 60, textAlign: "center" },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => openEdit(r.id)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Conceptos de Nómina" subtitle="Percepciones y deducciones" />

      <TableToolbar
        search={search}
        onSearch={setSearch}
        extraLeft={
          <select className="lmd-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ width: 150 }}>
            <option value="">Todos</option>
            <option value="P">Percepciones</option>
            <option value="D">Deducciones</option>
            <option value="O">Otros</option>
          </select>
        }
        extraRight={
          <button className="lmd-btn lmd-btn-primary" onClick={openNew}>+ Nuevo concepto</button>
        }
      />

      <DataTable columns={columns} rows={filtered} keyField="Id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 520, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 17 }}>{editId ? "Editar Concepto" : "Nuevo Concepto"}</h3>

            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Clave *</label>
                  <input className="lmd-input" name="Clave" value={form.Clave} onChange={handleChange} required style={{ width: "100%" }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Tipo *</label>
                  <select className="lmd-select" name="TipoConcepto" value={form.TipoConcepto} onChange={handleChange} required style={{ width: "100%" }}>
                    <option value="P">Percepción</option>
                    <option value="D">Deducción</option>
                    <option value="O">Otro</option>
                  </select>
                </div>
                <div style={{ marginBottom: 12, gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Nombre *</label>
                  <input className="lmd-input" name="Nombre" value={form.Nombre} onChange={handleChange} required style={{ width: "100%" }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Clave SAT (CFDI nómina)</label>
                  <input className="lmd-input" name="ClaveSat" value={form.ClaveSat} onChange={handleChange} style={{ width: "100%" }} placeholder="001, 002..." />
                </div>
              </div>

              <div style={{ display: "flex", gap: 24, marginTop: 4, marginBottom: 20 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" name="GravaImss" checked={form.GravaImss} onChange={handleChange} />
                  Grava IMSS
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" name="GravaIsr" checked={form.GravaIsr} onChange={handleChange} />
                  Grava ISR
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" name="Activo" checked={form.Activo} onChange={handleChange} />
                  Activo
                </label>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : editId ? "Guardar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
